import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/cn';
import { safeAppendChild, safeRemoveChild, isNodeInDocument } from '@/utils/dom-helpers';
import { logError, logWarning } from '@/utils/error-logger';
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  
  // Auto-close after duration
  useEffect(() => {
    if (duration === Infinity) return;
    
    const timer = setTimeout(() => {
      handleClose();
    }, duration);
    
    return () => clearTimeout(timer);
  }, [duration]);
  
  // Handle close with animation
  const handleClose = () => {
    setIsExiting(true);
    
    // Wait for animation to complete
    setTimeout(() => {
      setIsVisible(false);
      onClose(id);
    }, 300);
  };
  
  // Get icon based on type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };
  
  // Get background color based on type
  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
    }
  };
  
  if (!isVisible) return null;
  
  return (
    <div
      className={cn(
        'max-w-sm w-full shadow-lg rounded-lg pointer-events-auto border overflow-hidden',
        getBackgroundColor(),
        isExiting ? 'animate-fade-out' : 'animate-fade-in',
      )}
      role="alert"
      aria-live="assertive"
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium text-gray-900">{title}</p>
            {message && (
              <p className="mt-1 text-sm text-gray-500">{message}</p>
            )}
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className="bg-transparent rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleClose}
              aria-label="Fermer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast container
export const ToastContainer: React.FC<{
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}> = ({
  position = 'top-right'
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    try {
      if (!document.body) {
        logWarning('ToastContainer: document.body not available');
        return;
      }

      let container = document.getElementById('toast-container') as HTMLDivElement;

      if (!container) {
        container = document.createElement('div');
        container.id = 'toast-container';

        const appendResult = safeAppendChild(document.body, container);

        if (!appendResult.success) {
          logError('ToastContainer: failed to append container', {
            location: 'ToastContainer:mount',
            hasBody: !!document.body,
            bodyChildren: document.body.childNodes.length
          }, appendResult.error);
          return;
        }
      }

      containerRef.current = container;
      isMountedRef.current = true;

      return () => {
        isMountedRef.current = false;

        if (!containerRef.current) return;

        try {
          if (!isNodeInDocument(containerRef.current)) {
            containerRef.current = null;
            return;
          }

          if (containerRef.current.childNodes.length === 0) {
            const removeResult = safeRemoveChild(document.body, containerRef.current);

            if (!removeResult.success) {
              logError('ToastContainer: cleanup failed', {
                location: 'ToastContainer:cleanup',
                containerInDocument: isNodeInDocument(containerRef.current),
                hasParent: !!containerRef.current.parentNode,
                childCount: containerRef.current.childNodes.length
              }, removeResult.error);
            }
          }

          containerRef.current = null;
        } catch (error) {
          logError('ToastContainer: cleanup error', {
            location: 'ToastContainer:cleanup'
          }, error as Error);
        }
      };
    } catch (error) {
      logError('ToastContainer: unexpected error', {
        location: 'ToastContainer'
      }, error as Error);
    }
  }, []);

  const getPositionClasses = () => {
    switch (position) {
      case 'top-right':
        return 'top-4 right-4';
      case 'top-left':
        return 'top-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-center':
        return 'top-4 left-1/2 transform -translate-x-1/2';
      case 'bottom-center':
        return 'bottom-4 left-1/2 transform -translate-x-1/2';
    }
  };

  const container = containerRef.current || document.getElementById('toast-container');

  if (!container || !isMountedRef.current) return null;

  try {
    return createPortal(
      <div
        className={cn(
          'fixed z-50 flex flex-col space-y-4 max-h-screen overflow-hidden',
          getPositionClasses()
        )}
      >
        {/* Toasts will be rendered here */}
      </div>,
      container
    );
  } catch (error) {
    logError('ToastContainer: createPortal failed', {
      location: 'ToastContainer:createPortal',
      hasContainer: !!container,
      containerInDocument: container ? isNodeInDocument(container) : false
    }, error as Error);
    return null;
  }
};

// Toast context
interface ToastContextType {
  showToast: (props: Omit<ToastProps, 'id' | 'onClose'>) => string;
  hideToast: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined);

// Toast provider
export const ToastProvider: React.FC<{
  children: React.ReactNode;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
}> = ({
  children,
  position = 'top-right'
}) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      setToasts([]);
    };
  }, []);

  const showToast = (props: Omit<ToastProps, 'id' | 'onClose'>) => {
    if (!isMountedRef.current) {
      return '';
    }

    const id = Date.now().toString();

    setToasts(prev => {
      if (!isMountedRef.current) return prev;

      return [
        ...prev,
        {
          ...props,
          id,
          onClose: hideToast
        }
      ];
    });

    return id;
  };

  const hideToast = (id: string) => {
    if (!isMountedRef.current) {
      return;
    }

    setToasts(prev => {
      if (!isMountedRef.current) return prev;
      return prev.filter(toast => toast.id !== id);
    });
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}

      {isMountedRef.current && (
        <div className={cn(
          'fixed z-50 flex flex-col space-y-4 max-h-screen overflow-hidden pointer-events-none',
          position === 'top-right' && 'top-4 right-4',
          position === 'top-left' && 'top-4 left-4',
          position === 'bottom-right' && 'bottom-4 right-4',
          position === 'bottom-left' && 'bottom-4 left-4',
          position === 'top-center' && 'top-4 left-1/2 transform -translate-x-1/2',
          position === 'bottom-center' && 'bottom-4 left-1/2 transform -translate-x-1/2'
        )}>
          {toasts.map(toast => (
            <Toast key={toast.id} {...toast} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
};

// Hook to use toast
export const useToast = () => {
  const context = React.useContext(ToastContext);
  
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  
  return context;
};

export default Toast;